/**
 * avatarDB — 使用 IndexedDB 存储员工头像数据（base64 data URLs）
 *
 * 为什么不用 localStorage：
 *   - localStorage 只有 5-10MB 限制
 *   - base64 图片每张约 40-130KB，数十张即超限
 *   - 超限后 setItem 静默失败，导致数据丢失
 *
 * IndexedDB 空间可达数百 MB，适合存储文件类数据。
 */

const DB_NAME = 'yh_avatar_db';
const STORE_NAME = 'avatars';
const DB_VERSION = 1;

let _dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      _dbPromise = null;
      reject(request.error);
    };
  });
  return _dbPromise;
}

/** 存储头像 data URL */
export async function setAvatar(id: string, dataUrl: string): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(dataUrl, id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn('[avatarDB] setAvatar failed', e);
  }
}

/** 读取单个头像 */
export async function getAvatar(id: string): Promise<string | undefined> {
  try {
    const db = await openDB();
    return await new Promise<string | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(id);
      req.onsuccess = () => resolve(req.result as string | undefined);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return undefined;
  }
}

/** 批量读取所有头像，返回 Map<id, dataUrl> */
export async function getAllAvatars(): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          result.set(String(cursor.key), cursor.value as string);
          cursor.continue();
        } else {
          resolve();
        }
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    // silently return whatever we got
  }
  return result;
}

/** 删除单个头像 */
export async function deleteAvatar(id: string): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // ignore
  }
}

/** 判断是否为 base64 data URL（需要存 IndexedDB 而非 localStorage） */
export function isBase64DataUrl(str: string | undefined): boolean {
  return typeof str === 'string' && str.startsWith('data:');
}

/** IDB 中有头像时，localStorage 里的占位符 */
export const IDB_AVATAR_MARKER = '__idb_avatar__';
